import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import ReactPlayer from "react-player";
import { Hash, Radio, X } from "lucide-react";

import { PageWrapper } from "../../../../components/layout/container/Container";
import VideoSkeletonCard from "../../../../components/cards/skeleton/VideoSkeletonCard";

import { FormatDateToLocaleDateString } from "../../../../utils/DateTime";

import { useCreatorStreams } from "../../creators/hooks/useCreatorStreams";

import "./StreamReplay.css";

const StreamReplay = ({ streamSessionId, isOpen = true, onClose }) => {
  const {
    stream,
    loading,
    error,

    getStreamSessionById,
    resetStreamState,
  } = useCreatorStreams();

  useEffect(() => {
    if (!isOpen || !streamSessionId) {
      resetStreamState();

      return;
    }

    getStreamSessionById(streamSessionId);

    return () => {
      resetStreamState();
    };
  }, [isOpen, streamSessionId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousTitle = document.title;

    document.title = `Katch | ${stream?.title || "Stream Replay"}`;

    return () => {
      document.title = previousTitle;
    };
  }, [isOpen, stream?.title]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  const getErrorMessage = () => {
    if (!error) {
      return "";
    }

    if (typeof error === "string") {
      return error;
    }

    return (
      error?.response?.data?.message ||
      error?.message ||
      "Unable to load this stream replay."
    );
  };

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  const renderCloseButton = () => (
    <button
      type="button"
      className="stream-replay-modal__close"
      onClick={onClose}
      aria-label="Close stream replay"
    >
      <X size={18} />
    </button>
  );

  const renderEmptyState = ({ title, message }) => (
    <main className="stream-replay">
      <div className="stream-replay__empty-shell">
        {renderCloseButton()}

        <div className="stream-replay__empty">
          <div className="stream-replay__empty-icon">
            <Radio size={26} />
          </div>

          <h1>{title}</h1>
          <p>{message}</p>
        </div>
      </div>
    </main>
  );

  const renderContent = () => {
    if (!streamSessionId) {
      return renderEmptyState({
        title: "Replay unavailable",
        message: "This stream replay link is incomplete.",
      });
    }

    if (loading && !stream?.streamSessionId) {
      return (
        <main className="stream-replay stream-replay--loading">
          <div className="stream-replay__player-skeleton">
            {renderCloseButton()}
          </div>

          <VideoSkeletonCard />
        </main>
      );
    }

    if (error) {
      return renderEmptyState({
        title: "Replay unavailable",
        message: getErrorMessage(),
      });
    }

    if (!stream?.streamSessionId) {
      return renderEmptyState({
        title: "Replay unavailable",
        message: "This stream could not be found.",
      });
    }

    const replayUrl =
      stream.vodHlsUrl ||
      stream.vod_hls_url ||
      stream.vodMp4Url ||
      stream.vod_mp4_url ||
      stream.recordingPlaybackUrl ||
      stream.recording_playback_url ||
      stream.replayUrl ||
      stream.replay_url ||
      "";

    const thumbnailUrl = stream.thumbnailUrl || stream.thumbnail_url || "";

    if (!replayUrl) {
      return renderEmptyState({
        title: "Replay not ready",
        message: "This stream replay is still being prepared.",
      });
    }

    const creatorName =
      stream.brandName ||
      stream.brand_name ||
      stream.creatorName ||
      stream.creator_name ||
      stream.username ||
      "Katch Creator";

    const creatorInitial = creatorName.charAt(0).toUpperCase();

    const profileImageUrl =
      stream.profileImageUrl || stream.profile_image_url || "";

    const createdAt =
      stream.createdAt ||
      stream.created_at ||
      stream.actualStart ||
      stream.actual_start;

    const formattedDate = createdAt
      ? FormatDateToLocaleDateString(createdAt)
      : null;

    return (
      <main className="stream-replay">
        <section>
          <div
            className="stream-replay__player-shell"
            onContextMenu={(event) => event.preventDefault()}
          >
            {renderCloseButton()}

            <ReactPlayer
              key={streamSessionId}
              className="stream-replay__player"
              src={replayUrl}
              width="100%"
              height="100%"
              controls
              playsinline
              onError={(error) => {
                console.error("Stream replay playback error:", error);
              }}
              config={{
                file: {
                  attributes: {
                    preload: "metadata",
                    playsInline: true,
                    poster: thumbnailUrl,
                    disablePictureInPicture: true,
                    controlsList: "nodownload noplaybackrate",
                    onContextMenu: (event) => event.preventDefault(),
                  },
                },
              }}
            />
          </div>
        </section>

        <section className="stream-replay__info">
          <h1 className="stream-replay__title">
            {stream.title || "Untitled Stream"}
          </h1>

          <div className="stream-replay__toolbar">
            <div className="stream-replay__channel-info">
              <div className="stream-replay__channel-avatar">
                {profileImageUrl ? (
                  <img src={profileImageUrl} alt={creatorName} />
                ) : (
                  <span>{creatorInitial}</span>
                )}
              </div>

              <div className="stream-replay__channel-meta">
                <span className="stream-replay__channel-name">
                  {creatorName}
                </span>

                {stream.username && (
                  <span className="stream-replay__channel-username">
                    {stream.username}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="stream-replay__description-card">
            {(formattedDate || stream.sport) && (
              <div className="stream-replay__meta">
                {formattedDate && (
                  <div className="stream-replay__meta-item">
                    <span>{formattedDate}</span>
                  </div>
                )}

                {stream.sport && (
                  <div className="stream-replay__sport-tag">
                    <Hash size={13} />
                    <span>{stream.sport}</span>
                  </div>
                )}
              </div>
            )}

            <p className="stream-replay__description-text">
              {stream.description?.trim() ||
                "No description has been added for this replay."}
            </p>
          </div>
        </section>
      </main>
    );
  };

  return createPortal(
    <div
      className="stream-replay-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Stream replay player"
    >
      <div className="stream-replay-modal__overlay" onClick={onClose} />

      <div className="stream-replay-modal__content">
        <PageWrapper size="lg">{renderContent()}</PageWrapper>
      </div>
    </div>,
    document.body,
  );
};

export default StreamReplay;
